import { ReactNode, useMemo } from "react";
import { cn } from "@/lib/utils";
import { sanitizeRichHtml, splitAtVoces, stripVoces } from "../richText";

interface RichContentProps {
  /** HTML del editor visual (se sanitiza acá antes de mostrarlo). */
  html: string;
  className?: string;
  /** Qué mostrar donde se ubicó el bloque "Voces de la Red" en el editor (el renglón para
   *  responder). Sin esto, el bloque no se muestra. */
  renderVoces?: () => ReactNode;
}

/**
 * Muestra el contenido con formato del newsletter y las novedades de Comunidad tal como se
 * armó en el editor visual del panel: títulos, colores de la marca, listas, links, imágenes,
 * banners y stickers. Siempre pasa por `sanitizeRichHtml` (solo etiquetas y colores
 * permitidos), así que es seguro aunque el HTML venga de la base.
 */
export const RichContent = ({ html, className, renderVoces }: RichContentProps) => {
  const safe = useMemo(() => sanitizeRichHtml(html), [html]);
  const parts = useMemo(() => (renderVoces ? splitAtVoces(safe) : null), [safe, renderVoces]);
  const plain = useMemo(() => (parts ? "" : stripVoces(safe)), [safe, parts]);
  return (
    <div
      className={cn(
        "text-base leading-[1.75] text-[#2D2049] md:text-[17px]",
        "[&_p]:my-3 [&_p:empty]:min-h-[1em]",
        "[&_h1]:mb-2 [&_h1]:mt-8 [&_h1]:text-[26px] [&_h1]:font-extrabold [&_h1]:leading-tight [&_h1]:text-[#2D2049] [&_h1:first-child]:mt-2 md:[&_h1]:text-[30px]",
        "[&_h2]:mb-1 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#665090] md:[&_h2]:text-xl",
        "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5",
        "[&_a]:font-semibold [&_a]:text-[#665090] [&_a]:underline",
        "[&_blockquote]:border-l-4 [&_blockquote]:border-[#C9BCE8] [&_blockquote]:pl-3 [&_blockquote]:italic",
        "[&_img]:mx-auto [&_img]:my-3 [&_img]:block [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-xl",
        "[&_img[data-variant=banner]]:w-full [&_img[data-variant=banner]]:object-cover",
        "[&_img[data-variant=inline]]:max-h-[420px] [&_img[data-variant=inline]]:object-contain",
        "[&_img[data-variant=sticker]]:max-h-[180px] [&_img[data-variant=sticker]]:max-w-[220px] [&_img[data-variant=sticker]]:object-contain",
        className,
      )}
      // eslint-disable-next-line react/no-danger
      {...(parts ? {} : { dangerouslySetInnerHTML: { __html: plain } })}
    >
      {parts && renderVoces && (
        <>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: parts.before }} />
          <div className="my-4">{renderVoces()}</div>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: parts.after }} />
        </>
      )}
    </div>
  );
};

export default RichContent;
